<template>
  <v-container id="regular-forms" fluid tag="section">
    <v-row>
      <v-col cols="12">
        <base-material-card
          color="primary"
          icon="mdi-file-document"
          :title="
            editOrderLine
              ? $t('Edit Orderline')
              : $t('New Orderline') +
                ' [' +
                $t('Doortype') +
                ' ' +
                doorType.code +
                ']'
          "
          class="px-5 py-3"
        >
          <v-row v-if="$auth.user().permissions.includes('doorType.update')">
            <v-col>
              <a target="_blank" :href="'/doortype/' + doorType.id"
                ><v-btn class="float-right" color="primary">{{
                  doorType.code
                }}</v-btn></a
              >
            </v-col>
          </v-row>

          <v-progress-linear
            indeterminate
            rounded
            height="6"
            v-if="loading"
          ></v-progress-linear>
          <v-form
            v-else
            ref="form"
            autocomplete="new-password"
            lazy-validation
            @submit.prevent="saveItem"
          >
            <v-spacer></v-spacer>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="formValues.position"
                  :label="$t('position') + '*'"
                  type="number"
                  @wheel="$event.preventDefault()"
                  v-validate="'required'"
                  data-vv-name="position"
                  :error-messages="errors.collect('position')"
                ></v-text-field>
              </v-col>
              <v-col v-if="computedEdgespacers.length > 0">
                <v-text-field
                  v-model="formValues.number_customer"
                  :label="$t('number_customer') + '*'"
                  v-validate="'required'"
                  data-vv-name="number_customer"
                  :error-messages="errors.collect('number_customer')"
                ></v-text-field>
              </v-col>
              <v-col v-else>
                <v-text-field
                  v-model="formValues.number_customer"
                  :label="$t('number_customer')"
                  v-validate="''"
                  data-vv-name="number_customer"
                  :error-messages="errors.collect('number_customer')"
                ></v-text-field>
              </v-col>
              <v-col v-if="formValues.icons && formValues.icons.length > 1">
                <v-select
                  v-model="formValues.icon_id"
                  :items="formValues.icons"
                  :label="$t('Icon')"
                  item-text="name"
                  item-value="id"
                  v-validate="''"
                  data-vv-name="icon_id"
                  :error-messages="errors.collect('icon_id')"
                  :disabled="formValues.icons.length == 1"
                >
                  <template v-slot:item="{ item }">
                    <img
                      :src="item.thumb | addToken"
                      width="40"
                      max-width="40"
                      class="p-2"
                    />
                  </template>
                  <template v-slot:selection="{ item }">
                    <img
                      :src="item.thumb | addToken"
                      width="40"
                      max-width="40"
                      class="p-2"
                    />
                  </template>
                </v-select>
              </v-col>
              <v-col v-else>
                <v-img
                  :src="doorType.icon"
                  max-height="80"
                  contain
                  :title="doorType.code + ' - ' + doorType.name"
                >
                  <p>{{ doorType.code }}</p>
                </v-img>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="formValues.width"
                  :label="$t('width') + '*'"
                  :placeholder="
                    doorType.width_min +
                      ' - ' +
                      doorType.width_max +
                      ' ' +
                      $t('mm')
                  "
                  persistent-placeholder
                  type="number"
                  @wheel="$event.preventDefault()"
                  required
                  v-validate="'required'"
                  data-vv-name="width"
                  :error-messages="errors.collect('width')"
                  @change="calcWidthDimensions()"
                  @blur="
                    glassPercentage();
                    calcWeight();
                  "
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="formValues.height"
                  :label="$t('height') + '*'"
                  :placeholder="
                    doorType.height_min +
                      ' - ' +
                      doorType.height_max +
                      ' ' +
                      $t('mm')
                  "
                  persistent-placeholder
                  type="number"
                  @wheel="$event.preventDefault()"
                  required
                  v-validate="'required'"
                  data-vv-name="height"
                  :error-messages="errors.collect('height')"
                  @change="calcHeightDimensions()"
                  @blur="
                    glassPercentage();
                    calcWeight();
                  "
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  :label="$t('open width')"
                  :value="this.formValues.open_width"
                  disabled
                >
                </v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  :label="$t('open height')"
                  :value="this.formValues.open_height"
                  disabled
                >
                </v-text-field>
              </v-col>
              <v-col>
                <v-select
                  v-model="formValues.opening_direction"
                  :items="[
                    {
                      id: 'in',
                      name: $t('in')
                    },
                    {
                      id: 'out',
                      name: $t('out')
                    }
                  ]"
                  :label="$t('opening direction') + '*'"
                  item-text="name"
                  item-value="id"
                  v-validate="'required'"
                  data-vv-name="opening_direction"
                  :error-messages="errors.collect('opening_direction')"
                ></v-select>
              </v-col>
              <v-col v-if="internalDoorType.frame_thickness_min">
                <v-text-field
                  v-model="formValues.frame_thickness"
                  :label="$t('Frame thickness')"
                  type="number"
                  @wheel="$event.preventDefault()"
                  required
                  v-validate="
                    'required|min_value:' + internalDoorType.frame_thickness_min
                  "
                  data-vv-name="frame_thickness"
                  :error-messages="errors.collect('frame_thickness')"
                ></v-text-field>
              </v-col>
              <v-col v-if="internalDoorType.frame_width_min">
                <v-text-field
                  v-if="
                    !(
                      formValues.frame_width != formValues.frame_width_left ||
                      formValues.frame_width != formValues.frame_width_right
                    )
                  "
                  v-model="formValues.frame_width"
                  :label="$t('Frame width')"
                  type="number"
                  @wheel="$event.preventDefault()"
                  required
                  v-validate="
                    'required|min_value:' + internalDoorType.frame_width_min
                  "
                  data-vv-name="frame_width"
                  :data-vv-as="$t('frame_width')"
                  :error-messages="errors.collect('frame_width')"
                  @change="
                    formValues.frame_width_left = formValues.frame_width;
                    formValues.frame_width_right = formValues.frame_width;
                    calcWidthDimensions();
                    calcHeightDimensions();
                  "
                  append-outer-icon="mdi-cogs"
                  @click:append-outer="openFrameDialog"
                ></v-text-field>

                <v-row v-else class="mt-4" align="center" no-gutters>
                  <v-col cols="12" class="d-flex align-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          v-on="on"
                          :value="frameWidthsInline"
                          :label="$t('Frame width different')"
                          readonly
                          disabled
                          dense
                          class="flex-grow-1"
                        ></v-text-field>
                      </template>
                      <span>
                        {{ $t("Frame width top") }} /
                        {{ $t("Frame width left") }} /
                        {{ $t("Frame width right") }}
                      </span>
                    </v-tooltip>
                    <v-btn
                      icon
                      color="primary"
                      class="ml-2"
                      @click="openFrameDialog"
                      :aria-label="$t('Edit')"
                      :title="$t('Edit')"
                    >
                      <v-icon>mdi-cogs</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-alert
                  v-if="frameDialogErrorSummary.length"
                  type="error"
                  dense
                  text
                  class="mt-2"
                >
                  <div v-for="(msg, i) in frameDialogErrorSummary" :key="i">
                    {{ msg }}
                  </div>
                </v-alert>
                <v-dialog v-model="frameDialog" width="500" persistent>
                  <v-card>
                    <v-card-title class="text-h5 grey lighten-2">
                      {{ $t("Frame width") }} {{ $t("Edit") }}
                    </v-card-title>

                    <v-card-text>
                      <v-text-field
                        v-model="formValues.frame_width"
                        :label="$t('Frame width top')"
                        type="number"
                        @wheel="$event.preventDefault()"
                        required
                        v-validate="
                          'required|min_value:' +
                            internalDoorType.frame_width_min
                        "
                        data-vv-name="frame_width"
                        :data-vv-as="$t('frame_width_top')"
                        :error-messages="errors.collect('frame_width')"
                        @change="
                          calcWidthDimensions();
                          calcHeightDimensions();
                        "
                      ></v-text-field>
                      <v-text-field
                        v-model="formValues.frame_width_left"
                        :label="$t('Frame width left')"
                        type="number"
                        @wheel="$event.preventDefault()"
                        required
                        v-validate="
                          'required|min_value:' +
                            internalDoorType.frame_width_min
                        "
                        data-vv-name="frame_width_left"
                        :data-vv-as="$t('frame_width_left')"
                        :error-messages="errors.collect('frame_width_left')"
                        @change="
                          calcWidthDimensions();
                          calcHeightDimensions();
                        "
                      ></v-text-field>
                      <v-text-field
                        v-model="formValues.frame_width_right"
                        :label="$t('Frame width right')"
                        type="number"
                        @wheel="$event.preventDefault()"
                        required
                        v-validate="
                          'required|min_value:' +
                            internalDoorType.frame_width_min
                        "
                        data-vv-name="frame_width_right"
                        :data-vv-as="$t('frame_width_right')"
                        :error-messages="errors.collect('frame_width_right')"
                        @change="
                          calcWidthDimensions();
                          calcHeightDimensions();
                        "
                      ></v-text-field>
                      <v-text-field
                        v-model="formValues.ground_distance"
                        :label="$t('ground_distance')"
                        type="number"
                        @wheel="$event.preventDefault()"
                        required
                        v-validate="'required'"
                        data-vv-name="ground_distance"
                        :data-vv-as="$t('ground_distance')"
                        :error-messages="errors.collect('ground_distance')"
                        @change="
                          calcWidthDimensions();
                          calcHeightDimensions();
                        "
                      ></v-text-field>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" text @click="tryCloseFrameDialog">
                        OK
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-col>
            </v-row>

            <v-divider></v-divider>
            <v-row>
              <v-col cols="6"
                ><h3>{{ $t("components") }}:</h3>
              </v-col>
              <v-col cols="6"
                ><h3>
                  {{ $t("extracts") }}:
                  <v-tooltip bottom v-if="internalDoorType.glass_info">
                    <template v-slot:activator="{ on }">
                      <v-icon color="primary" dark v-on="on"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>{{ internalDoorType.glass_info }}</span>
                  </v-tooltip>
                </h3>
              </v-col>
            </v-row>
            <v-row
              v-for="(component, index) in internalDoorType.components"
              :key="component.id"
            >
              <v-col>
                <v-card class="pa-2 orderline component">
                  <v-row>
                    <v-col cols="4">
                      <v-row>
                        <v-col cols="12">
                          {{ component.description }}
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            v-model="
                              formValues.components[component.type].width
                            "
                            :label="$t('width') + '*'"
                            type="number"
                            @wheel="$event.preventDefault()"
                            required
                            outlined
                            v-validate="'required'"
                            :data-vv-name="
                              'components.' + component.type + '.width'
                            "
                            :disabled="
                              !(
                                component.width_editable && width_height_checked
                              )
                            "
                            :error-messages="
                              errors.collect(
                                'components.' + component.type + '.width'
                              )
                            "
                            @change="calcComponentWidths()"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            v-model="
                              formValues.components[component.type].height
                            "
                            :label="$t('height') + '*'"
                            type="number"
                            @wheel="$event.preventDefault()"
                            required
                            outlined
                            v-validate="'required'"
                            :data-vv-name="
                              'components.' + component.type + '.height'
                            "
                            :disabled="
                              !(
                                component.height_editable &&
                                width_height_checked
                              )
                            "
                            :error-messages="
                              errors.collect(
                                'components.' + component.type + '.height'
                              )
                            "
                            @change="calcHeightDimensions(true)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            v-model="
                              formValues.components[component.type].weight
                            "
                            :label="$t('Weight')"
                            v-validate=""
                            readonly
                            disabled
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-autocomplete
                            v-model="
                              formValues.components[component.type].filling_id
                            "
                            :items="computedFillingItmes[component.type]"
                            :label="$t('fillings') + '*'"
                            :value="
                              selectFirstComponentFilling(
                                computedFillingItmes[component.type],
                                component
                              )
                            "
                            v-if="computedFillingItmes[component.type].length"
                            item-text="name"
                            item-value="id"
                            required
                            v-validate="'required'"
                            :data-vv-name="
                              'components.' +
                                computedFillingItmes[component.type] +
                                '.filling_id'
                            "
                            :error-messages="
                              errors.collect(
                                'components.' + component.type + '.filling_id'
                              )
                            "
                            :disabled="
                              !(
                                computedFillingItmes[component.type].length !=
                                  1 && width_height_checked
                              )
                            "
                            @change="
                              glassPercentage();
                              prove();
                            "
                          ></v-autocomplete>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="2"></v-col>
                    <v-col cols="6" v-if="component.extracts_allowed">
                      <v-row
                        v-for="(extract, key) in formValues.components[
                          component.type
                        ].extract_entries"
                        :key="extract.id"
                      >
                        <v-col cols="12"
                          >{{ $t("extract") + " " + (key + 1) }}:</v-col
                        >
                        <v-col cols="2">
                          <v-text-field
                            v-model="extract.width"
                            :label="$t('width')"
                            type="number"
                            @wheel="$event.preventDefault()"
                            required
                            v-validate="'required'"
                            data-vv-name="'components.'+index+'.extract_entries.'+key+'.width'"
                            :error-messages="
                              errors.collect(
                                'components.' +
                                  index +
                                  '.extract_entries.' +
                                  key +
                                  '.width'
                              )
                            "
                            :disabled="!width_height_checked"
                            @input="calcArea(extract)"
                            @blur="glassPercentage()"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="2">
                          <v-text-field
                            v-model="extract.height"
                            :label="$t('height')"
                            type="number"
                            @wheel="$event.preventDefault()"
                            required
                            v-validate="'required'"
                            data-vv-name="'components.'+index+'.extract_entries.'+key+'.height'"
                            :error-messages="
                              errors.collect(
                                'components.' +
                                  index +
                                  '.extract_entries.' +
                                  key +
                                  '.height'
                              )
                            "
                            :disabled="!width_height_checked"
                            @input="calcArea(extract)"
                            @blur="glassPercentage()"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="2">
                          <v-text-field
                            v-model="extract.area"
                            :label="$t('area') + '*'"
                            type="number"
                            @wheel="$event.preventDefault()"
                            required
                            v-validate="'required'"
                            data-vv-name="'components.'+index+'.extract_entries.'+key+'.area'"
                            :error-messages="
                              errors.collect(
                                'components.' +
                                  index +
                                  '.extract_entries.' +
                                  key +
                                  '.area'
                              )
                            "
                            :disabled="!width_height_checked"
                            @input="changeArea(extract)"
                            @blur="glassPercentage()"
                            @mouseleave="prove()"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="3">
                          <v-select
                            v-model="extract.filling_id"
                            :items="extract.selectValues"
                            :label="$t('filling') + ' *'"
                            item-text="name"
                            item-value="id"
                            v-validate=""
                            :data-vv-name="
                              'components.' +
                                index +
                                '.extract_entries.' +
                                key +
                                '.filling_id'
                            "
                            :error-messages="
                              errors.collect(
                                'components.' +
                                  index +
                                  '.extract_entries.' +
                                  key +
                                  '.filling_id'
                              )
                            "
                            @change="prove()"
                            :disabled="!width_height_checked"
                          ></v-select>
                        </v-col>
                        <v-col>
                          <v-icon
                            v-if="
                              component.extracts_qty == null ||
                                key >= component.extracts_qty
                            "
                            color="primary"
                            dark
                            @click="
                              removeExtract(
                                formValues.components[component.type]
                                  .extract_entries,
                                key
                              )
                            "
                            >mdi-minus-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            color="primary"
                            class="mr-4"
                            :disabled="!width_height_checked"
                            v-if="
                              formValues.components[component.type]
                                .extract_entries.length <
                                component.extracts_qty_max ||
                                component.extracts_qty_max == null
                            "
                            @click="
                              addExtract(
                                formValues.components[component.type]
                                  .extract_entries,
                                formValues.components[component.type]
                                  .selectValues
                              )
                            "
                          >
                            {{ $t("extract") + " " + $t("add") }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col v-if="doorType.ud_value || doorType.ud_table_id">
                <v-text-field
                  v-model="formValues.ud_value"
                  :label="$t('Ud value')"
                  :title="$t(formValues.wood_type)"
                  readonly
                  outlined
                  :loading="proveLoading"
                ></v-text-field>
              </v-col>
              <v-col v-if="doorType.ud_value || doorType.ud_table_id">
                <v-text-field
                  v-model="formValues.ug_max"
                  :label="$t('Ug max')"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="formValues.glass_percentage"
                  :label="$t('Glass Percentage')"
                  readonly
                  v-validate="'max_value:95'"
                  :data-vv-name="'glass_percentage'"
                  :error-messages="errors.collect('glass_percentage')"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col v-if="doorType.rw_value">
                <v-text-field
                  v-model="doorType.rw_value"
                  :label="$t('Rw value') + ' [dB]'"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="formValues.weight"
                  :label="$t('Weight')"
                  v-validate=""
                  :data-vv-name="'weight'"
                  :error-messages="errors.collect('weight')"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="formValues.wall_connection_id"
                  :items="computedWallconnections"
                  :label="$t('wall connection') + '*'"
                  item-text="name"
                  item-value="id"
                  v-validate=""
                  data-vv-name="wall_connection_id"
                  :disabled="computedWallconnections.length == 1"
                  :error-messages="errors.collect('wall_connection_id')"
                  class="required"
                ></v-select>
              </v-col>
              <v-col v-if="computedEdgespacers.length > 0">
                <v-select
                  v-model="formValues.edge_spacer_id"
                  :items="computedEdgespacers"
                  :label="$t('edge_spacer')"
                  item-text="name"
                  item-value="id"
                  v-validate=""
                  clearable
                  data-vv-name="edge_spacer_id"
                  :disabled="computedEdgespacers.length == 1"
                  :error-messages="errors.collect('edge_spacer_id')"
                  class="required"
                ></v-select>
              </v-col>
              <v-col>
                <v-select
                  v-model="formValues.door_frame_id"
                  :items="computedFrames"
                  :label="$t('door frame') + '*'"
                  item-text="name"
                  item-value="id"
                  v-validate="'required'"
                  data-vv-name="door_frame_id"
                  :disabled="computedFrames.length == 1"
                  :error-messages="errors.collect('door_frame_id')"
                  @change="prove()"
                  class="required"
                ></v-select>
              </v-col>
            </v-row>
            <v-select
              v-for="door_type_item_group in internalDoorType.door_type_item_groups"
              :key="door_type_item_group.id"
              v-model="formValues.items[door_type_item_group.id]"
              :items="door_type_item_group.items"
              :label="
                door_type_item_group.required
                  ? door_type_item_group.name + '*'
                  : door_type_item_group.name
              "
              :class="door_type_item_group.required ? 'required' : ''"
              item-text="name"
              item-value="id"
              v-validate=""
              clearable
              :data-vv-name="'items.' + door_type_item_group.id"
              :error-messages="
                errors.collect('items.' + door_type_item_group.id)
              "
              :disabled="
                door_type_item_group.items.length == 1 &&
                  door_type_item_group.required == 1
              "
              @change="prove()"
            >
            </v-select>
            <v-card-actions class="pl-0">
              <v-btn color="primary" class="mr-4" @click="saveCallback()">
                {{ $t("back") }}
              </v-btn>
              <v-dialog v-model="dialog" max-width="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary float-right"
                    dark
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ $t("save_favorite") }}
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="headline">
                    {{ $t("save_favorite") }}
                  </v-card-title>
                  <v-card-text>
                    <v-btn
                      :color="newFavorite ? 'primary' : ''"
                      @click="newFavorite = true"
                      >{{ $t("favorite_create_new") }}</v-btn
                    >
                    <v-btn
                      :color="newFavorite ? '' : 'primary'"
                      @click="newFavorite = false"
                      >{{ $t("favorite_update_existing") }}</v-btn
                    >
                    <v-text-field
                      v-if="newFavorite"
                      v-model="favorite_name"
                      :label="$t('favorite_name') + '*'"
                      v-validate="''"
                      data-vv-name="name"
                      :error-messages="errors.collect('name')"
                    ></v-text-field>
                    <div v-else>
                      <h4 class="mt-2">
                        {{ $t("favorite_update_existing_message") }}:
                      </h4>
                      <v-select
                        v-model="favorite_id"
                        :items="favorites"
                        item-text="name"
                        item-value="id"
                        outlined
                      >
                      </v-select>
                    </div>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="green darken-1" text @click="dialog = false">
                      {{ $t("back") }}
                    </v-btn>
                    <v-btn color="green darken-1" text @click="_storeFavorite">
                      {{ $t("save") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <v-spacer></v-spacer>
              <v-btn color="primary" class="mr-4" @click="saveItem">
                {{ $t("save") }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </base-material-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  saveOrderline,
  fetchOrderline,
  fetchDoorTypes,
  fetchItems,
  fetchWallconnections,
  fetchFrames,
  fetchItemItems,
  prove,
  fetchEdgespacers,
  storeFavorite,
  fetchFavorite,
  fetchFavorites
} from "../shared/services";
export default {
  name: "orderlineform",
  components: {},
  $_veeValidate: {
    validator: "new"
  },
  props: {
    doorType: null,
    nextNumber: {
      default: 1
    },
    editOrderLine: null,
    saveCallback: null,
    favorite: null
  },
  data() {
    return {
      formValues: {
        number_customer: "",
        position: 1,
        opening_direction: "in",
        components: [],
        items: {},
        glass_percentage: 0,
        weight: 0,
        ug_max: 0,
        icons: []
        // frame_thickness: 0
      },
      loading: true,
      doorTypes: [],
      internalDoorType: null,
      items: [],
      wallconnections: [],
      edgespacers: [],
      favorites: [],
      frames: [],
      dialog: false,
      favorite_name: "",
      favorite_id: null,
      newFavorite: true,
      proveLoading: false,
      frameDialog: false,
      frameDialogErrorSummary: []
    };
  },
  mounted() {
    this.internalDoorType = this.doorType;
    this.init();
  },
  computed: {
    frameWidthsInline() {
      const top = this.formValues?.frame_width ?? "";
      const left = this.formValues?.frame_width_left ?? "";
      const right = this.formValues?.frame_width_right ?? "";
      return `${top} / ${left} / ${right}`.trim();
    },
    computedFormValues() {
      let components = [];
      Object.keys(this.formValues.components).map(key => {
        let area = 0;
        this.formValues.components[key].extract_entries.map(extract => {
          area = parseFloat(area) + parseFloat(extract.area);
        });
        components.push({
          type: key,
          area: area,
          ...this.formValues.components[key]
        });
      });
      return {
        ...this.formValues,
        door_type_id: this.internalDoorType.id,
        order_id: this.$route.params.id,
        components
      };
    },
    door_type_items() {
      var door_type_items = [];
      if (this.internalDoorType) {
        this.internalDoorType.door_type_items.map(door_type_item => {
          let obj = door_type_items.find(
            i => i.name == door_type_item.pivot.name
          );
          if (obj) {
            obj.items.push(door_type_item);
          } else {
            obj = {
              name: door_type_item.pivot.name,
              required: door_type_item.pivot.required,
              items: []
            };
            obj.items.push(door_type_item);
            door_type_items.push(obj);
          }
        });
      }
      return door_type_items;
    },
    computedWallconnections() {
      return this.internalDoorType.wallconnections.map(wallconnection => {
        return this.wallconnections.find(_wallconnection => {
          return _wallconnection.id == wallconnection;
        });
      });
    },
    computedEdgespacers() {
      return this.internalDoorType.edgespacers.map(edgespacer => {
        let edgspacer = this.edgespacers.find(_edgespacer => {
          return _edgespacer.id == edgespacer;
        });
        return edgspacer ? edgspacer : [];
      });
    },
    computedFrames() {
      return this.internalDoorType.frames.map(frame => {
        return this.frames.find(_frame => {
          return _frame.id == frame;
        });
      });
    },
    computedFillingItmes() {
      let components = {};
      this.internalDoorType.components.map(component => {
        let _items = component.fillings.map(filling => {
          return this.items.find(item => item.id == filling);
        });
        let cat_items = [];
        component.filling_categories.map(filling_category => {
          cat_items = [
            ...cat_items,
            ...this.items.filter(item => item.category_id == filling_category)
          ];
        });
        components[component.type] = [..._items, ...cat_items];
      });
      return components;
    },
    clearWidth() {
      return (
        parseInt(this.formValues.width) -
        parseInt(this.formValues.frame_width_left) -
        parseInt(this.formValues.frame_width_right) +
        2 * this.internalDoorType.overhang
      );
    },
    clearHeight() {
      return (
        this.formValues.height -
        parseInt(this.formValues.frame_width) +
        this.internalDoorType.overhang -
        parseInt(this.formValues.ground_distance)
      );
    },

    width_height_checked() {
      return !!(
        this.formValues.width &&
        this.formValues.height &&
        this.internalDoorType.width_min < this.formValues.width &&
        this.formValues.width <= this.internalDoorType.width_max &&
        this.internalDoorType.height_min < this.formValues.height &&
        this.formValues.height <= this.internalDoorType.height_max
      );
    }
  },
  methods: {
    openFrameDialog() {
      this.frameDialogErrorSummary = [];
      this.frameDialog = true;
    },
    async tryCloseFrameDialog() {
      // Keep the dialog open until all frame dialog fields are valid.
      const fields = [
        "frame_width",
        "frame_width_left",
        "frame_width_right",
        "ground_distance"
      ];

      const results = await Promise.all(
        fields.map(field => this.$validator.validate(field))
      );
      const isValid = results.every(Boolean);

      if (isValid) {
        this.frameDialogErrorSummary = [];
        this.frameDialog = false;
        return;
      }

      const summary = new Set();
      fields.forEach(field => {
        this.errors.collect(field).forEach(msg => summary.add(msg));
      });
      this.frameDialogErrorSummary = Array.from(summary);
      this.frameDialog = true;
      this.$forceUpdate();
    },
    async init() {
      let itemResponse = await fetchItemItems();
      this.items = itemResponse.data;

      fetchWallconnections().then(res => {
        this.wallconnections = res.data;
      });
      fetchEdgespacers().then(res => {
        this.edgespacers = res.data;
      });
      fetchFrames().then(res => {
        this.frames = res.data;
      });

      fetchFavorites().then(res => {
        this.favorites = res.data;
      });

      if (this.editOrderLine) {
        fetchOrderline(this.editOrderLine.id).then(res => {
          this.formValues = res.data;
          let _components = {};
          res.data.components.map(component => {
            let extract_entries = [];
            let _dtComponent = this.internalDoorType.components.find(
              dtComponent => dtComponent.type == component.type
            );
            if (_dtComponent.extracts_allowed) {
              let selectValues = _dtComponent.extract_fillings.map(filling => {
                return this.items.find(item => item.id == filling);
              });
              _dtComponent.extract_filling_categories.map(category => {
                let catalogItems = this.items.filter(
                  item => item.category_id == category
                );
                if (catalogItems) {
                  selectValues = [...selectValues, ...catalogItems];
                }
              });

              component.extract_entries.map(extract_entry => {
                extract_entry.selectValues = selectValues;
              });

              //sort selectvalues
              selectValues = selectValues.sort(function(a, b) {
                var nameA = a.name.toUpperCase();
                var nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              });

              component.selectValues = selectValues;
            }
            component = this.processComponent(component);

            _components[component.type] = component;
          });

          this.formValues.components = { ..._components };
          this.internalDoorType.components.map(component => {
            component = this.processComponent(component);
          });
          let items = {};
          this.internalDoorType.door_type_item_groups.map(item => {
            items[item.id] = res.data.items[item.id];
          });
          this.formValues.items = { ...items };
          this.formValues.icons = this.internalDoorType.documents.filter(
            file => {
              return file.type
                .replace(" ", "")
                .split(",")
                .includes("icons");
            }
          );
          this.loading = false;
          this.openWidth();
          this.openHeight();
          this.calcWeight();
          this.glassPercentage();
          this.prove();
        });
      } else if (this.favorite) {
        fetchFavorite(this.favorite.id).then(res => {
          res.data = res.data.configuration;
          this.formValues = res.data;
          let _components = {};
          this.formValues.position = this.nextNumber;
          this.formValues.number_customer = "";

          res.data.components.map(component => {
            let extract_entries = [];
            let _dtComponent = this.internalDoorType.components.find(
              dtComponent => dtComponent.type == component.type
            );
            if (_dtComponent.extracts_allowed) {
              let selectValues = _dtComponent.extract_fillings.map(filling => {
                return this.items.find(item => item.id == filling);
              });
              _dtComponent.extract_filling_categories.map(category => {
                let catalogItems = this.items.filter(
                  item => item.category_id == category
                );
                if (catalogItems) {
                  selectValues = [...selectValues, ...catalogItems];
                }
              });

              // for (let i = 0; i < component.extracts_qty; i++) {
              //   extract_entries.push({
              //     width: 0,
              //     height: 0,
              //     selectValues: this.removeDuplicates(selectValues, "id")
              //   });
              // }
              component.extract_entries.map(extract_entry => {
                extract_entry.selectValues = selectValues;
              });
              //sort selectvalues
              selectValues = selectValues.sort(function(a, b) {
                var nameA = a.name.toUpperCase();
                var nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              });
              component.selectValues = selectValues;
            }
            component = this.processComponent(component);

            _components[component.type] = component;
          });

          this.formValues.components = { ..._components };
          this.internalDoorType.components.map(component => {
            component = this.processComponent(component);
          });
          let items = {};
          this.internalDoorType.door_type_item_groups.map(item => {
            items[item.id] = res.data.items[item.id];
          });
          this.formValues.items = { ...items };
          this.formValues.icons = this.internalDoorType.documents.filter(
            file => {
              return file.type
                .replace(" ", "")
                .split(",")
                .includes("icons");
            }
          );
          this.loading = false;
        });
      } else {
        this.formValues.position = this.nextNumber;
        let _components = {};
        this.internalDoorType.components.map(component => {
          let extract_entries = [];
          let continuously_side_part = {};

          if (component.extracts_allowed) {
            let selectValues = component.extract_fillings.map(filling => {
              return this.items.find(item => item.id == filling);
            });
            component.extract_filling_categories.map(category => {
              let catalogItems = this.items.filter(
                item => item.category_id == category
              );
              if (catalogItems) {
                selectValues = [...selectValues, ...catalogItems];
              }
            });

            for (let i = 0; i < component.extracts_qty; i++) {
              extract_entries.push({
                width: 0,
                height: 0,
                selectValues: this.removeDuplicates(selectValues, "id")
              });
            }
            continuously_side_part = component.continuously_side_part;

            //sort selectvalues
            selectValues = selectValues.sort(function(a, b) {
              var nameA = a.name.toUpperCase();
              var nameB = b.name.toUpperCase();
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            });

            _components[component.type] = {
              selectValues,
              extract_entries,
              continuously_side_part
            };
          } else {
            continuously_side_part = component.continuously_side_part;

            _components[component.type] = {
              extract_entries,
              continuously_side_part
            };
          }
          component = this.processComponent(component);
        });
        this.formValues.components = { ..._components };

        let items = {};

        this.internalDoorType.door_type_item_groups.map(item => {
          items[item.id] = this.doorType.door_type_item_groups.find(
            ele => ele.id === item.id
          ).items[0].id;
        });
        this.formValues.items = { ...items };
        this.$set(this.formValues, "wood_type", "softwood");
        this.$set(
          this.formValues,
          "frame_thickness",
          this.internalDoorType.frame_thickness_min
        );
        this.$set(
          this.formValues,
          "frame_width",
          this.internalDoorType.frame_width_min
        );
        this.$set(
          this.formValues,
          "frame_width_left",
          this.internalDoorType.frame_width_min
        );
        this.$set(
          this.formValues,
          "frame_width_right",
          this.internalDoorType.frame_width_min
        );
        this.$set(this.formValues, "ground_distance", 5);
        this.$set(
          this.formValues,
          "wall_connection_id",
          this.internalDoorType.wallconnections[0]
        );

        // this.$set(
        //   this.formValues,
        //   "edge_spacer_id",
        //   this.internalDoorType.edgespacers[0]
        // );
        this.$set(
          this.formValues,
          "door_frame_id",
          this.internalDoorType.frames[0]
        );
        this.formValues.icons = this.internalDoorType.documents.filter(file => {
          return file.type
            .replace(" ", "")
            .split(",")
            .includes("icons");
        });
        this.$set(this.formValues, "icon_id", this.formValues.icons[0].id);
        this.loading = false;
      }
    },
    addExtract(extract_entries, selectValues) {
      extract_entries.push({
        height: 0,
        selectValues,
        width: 0
      });
    },
    removeExtract(extract_entries, id) {
      extract_entries.splice(id, 1);
      this.glassPercentage();
      this.prove();
    },
    getSelectArray(ids, values) {
      return ids.map(id => {
        return values.find(value => value.id == id);
      });
    },
    async saveItem(remain = false) {
      this.$validator.reset();
      this.$validator.validateAll();
      this.$forceUpdate();
      let resp = await saveOrderline(
        this.editOrderLine ? this.editOrderLine.id : null,
        this.computedFormValues,
        this.$validator
      );
      if (resp.success) {
        this.$root.$notification(resp.message, { type: "success" });
        this.saveCallback();
      } else {
        return Promise.reject();
      }
    },
    async _storeFavorite(remain = false) {
      this.$validator.reset();
      this.$validator.validateAll();
      this.$forceUpdate();
      let resp = await storeFavorite(
        null,
        {
          name: this.favorite_name,
          favorite_id: this.favorite_id,
          ...this.computedFormValues
        },
        this.$validator
      );
      if (resp.success) {
        this.$root.$notification(resp.message, { type: "success" });
        this.favorite_name = "";
        this.dialog = false;
        // this.saveCallback();
      } else {
        return Promise.reject();
      }
    },
    async prove() {
      if (this.computedFormValues.height && this.formValues.width) {
        this.$validator.reset();
        this.proveLoading = true;
        let resp = await prove(
          this.internalDoorType.id,
          {
            height: this.computedFormValues.height,
            width: this.computedFormValues.width,
            components: this.computedFormValues.components,
            items: this.computedFormValues.items,
            door_frame_id: this.computedFormValues.door_frame_id,
            glass_percentage: this.computedFormValues.glass_percentage
          },
          this.$validator
        );

        if (resp.success) {
          if (resp.u_value) {
            this.formValues.ud_value = resp.u_value.ud;
            this.formValues.ug_max = resp.u_value.ug;
            this.formValues.wood_type = resp.u_value.wood_type;
          }
        } else {
          this.formValues.ud_value = null;
          this.formValues.ug_max = null;
        }
        this.proveLoading = false;
        this.$forceUpdate();
      }
    },
    calcArea(extract) {
      extract.area = ((extract.width / 1000) * extract.height) / 1000;
      this.glassPercentage();
    },
    glassPercentage() {
      let glasspercentagebefore = this.formValues.glass_percentage;
      let area = 0;
      this.computedFormValues.components.map(component => {
        var filling = null;
        if (component.filling_id) {
          filling = this.computedFillingItmes[component.type].find(
            item => item.id == component.filling_id
          );
        }
        if (filling && filling.type == "glass") {
          area +=
            (component.width / 1000 -
              (parseInt(this.formValues.frame_width_left) +
                parseInt(this.formValues.frame_width_right)) /
                1000) *
            (component.height / 1000 -
              (2 * parseInt(this.formValues.frame_width)) / 1000);
        } else {
          //calc for all extracts
          component.extract_entries.map(ext => {
            area += parseFloat(ext.area);
          });
        }
      });
      let percentage = 0;
      percentage = Math.round(
        (area /
          (((this.formValues.width / 1000) * this.formValues.height) / 1000)) *
          100
      );
      this.formValues.glass_percentage = !Number.isNaN(percentage)
        ? percentage
        : 0;
      this.$forceUpdate();
      if (glasspercentagebefore != this.formValues.glass_percentage) {
        this.prove();
      }
    },
    calcWeight() {
      let weight =
        parseFloat(this.doorType.weight_per_qm) *
        (this.formValues.width / 1000) *
        (this.formValues.height / 1000);

      if (!Number.isNaN(weight)) {
        this.formValues.weight = parseFloat(weight).toFixed(1);
      }
      // calc component weights
      for (let value in this.formValues.components) {
        let weight =
          (((this.formValues.components[value].width / 1000) *
            this.formValues.components[value].height) /
            1000) *
          parseFloat(this.doorType.weight_per_qm);
        if (!Number.isNaN(weight)) {
          this.formValues.components[value].weight = parseFloat(weight).toFixed(
            1
          );
        }
      }
    },
    changeArea(extract) {
      extract.width = null;
      extract.height = null;
    },
    removeDuplicates(originalArray, prop) {
      var newArray = [];
      var lookupObject = {};

      for (var i in originalArray) {
        lookupObject[originalArray[i][prop]] = originalArray[i];
      }

      for (i in lookupObject) {
        newArray.push(lookupObject[i]);
      }
      return newArray;
    },
    calcWidthDimensions() {
      this.calcInitComponentWidths();
      this.calcOlWidth();
      this.openWidth();
      this.glassPercentage();
      this.prove();
    },
    calcHeightDimensions(flg_change = false) {
      this.formValues = { ...this.formValues };
      if (!flg_change) {
        this.calcInitComponentHeights();
      }

      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const flg2 = this.internalDoorType.components.find(
        component => component.type == "flg2"
      );
      const ol = this.internalDoorType.components.find(
        component => component.type == "ol"
      );
      const st1 = this.internalDoorType.components.find(
        component => component.type == "st1"
      );
      const st2 = this.internalDoorType.components.find(
        component => component.type == "st2"
      );
      let height = this.clearHeight;
      if (ol) {
        const total_height =
          (flg1?.proof_height || 0) + (ol?.proof_height || 0);

        const r_flg1 = flg1?.proof_height / total_height;
        height = Math.round(this.clearHeight * r_flg1);
      }

      if (flg1 && !flg_change) {
        this.$set(this.formValues.components.flg1, "height", height);
      }
      if (flg2) {
        this.$set(
          this.formValues.components.flg2,
          "height",
          this.formValues.components.flg1.height
        );
      }
      if (this.formValues.components.ol) {
        this.$set(
          this.formValues.components.ol,
          "height",
          this.clearHeight - this.formValues.components.flg1.height
        );
      }
      if (st1) {
        let st1_height = this.formValues.height;
        if (!st1.continuously_side_part && this.formValues.components.ol) {
          st1_height =
            this.formValues.height - this.formValues.components.ol.height;
        }
        this.$set(this.formValues.components.st1, "height", st1_height);
      }
      if (st2) {
        let st2_height = this.formValues.height;
        if (!st2.continuously_side_part && this.formValues.components.ol) {
          st2_height =
            this.formValues.height - this.formValues.components.ol.height;
        }
        this.$set(this.formValues.components.st2, "height", st2_height);
      }
      this.glassPercentage();
      this.openHeight();
      this.prove();
    },
    // Calculate "Oberlichte" dimensions
    calcOlWidth() {
      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const flg2 = this.internalDoorType.components.find(
        component => component.type == "flg2"
      );
      const st1 = this.internalDoorType.components.find(
        component => component.type == "st1"
      );
      const st2 = this.internalDoorType.components.find(
        component => component.type == "st2"
      );
      const ol = this.internalDoorType.components.find(
        component => component.type == "ol"
      );
      if (!ol) {
        return;
      }
      let st_width = 0;
      if (st1 && st1.continuously_side_part) {
        st_width += parseFloat(this.formValues.components.st1.width);
      }
      if (st2 && st2.continuously_side_part) {
        st_width += parseFloat(this.formValues.components.st2.width);
      }

      this.$set(
        this.formValues.components.ol,
        "width",
        this.formValues.width - st_width
      );
    },
    processComponent(component) {
      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const flg2 = this.internalDoorType.components.find(
        component => component.type == "flg2"
      );
      const st1 = this.internalDoorType.components.find(
        component => component.type == "st1"
      );
      const st2 = this.internalDoorType.components.find(
        component => component.type == "st2"
      );
      const ol = this.internalDoorType.components.find(
        component => component.type == "ol"
      );

      let val = "1";

      flg2 ? (val += "1") : (val += "0");
      st1 ? (val += "1") : (val += "0");
      st2 ? (val += "1") : (val += "0");

      component.height_editable = false;

      if (component.type == "flg1" && ol) {
        component.height_editable = true;
      }

      switch (val) {
        case "1010": //flg1 + st1
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "st1":
              component.width_editable = false;
              break;
          }
          break;
        case "1110": //flg1 + flg2 + st1
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "flg2":
              component.width_editable = true;
              break;
            case "st1":
              component.width_editable = false;
              break;
          }
          break;
        case "1111": //flg1 + flg2 + st1 + st2
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "flg2":
              component.width_editable = true;
              break;
            case "st1":
              component.width_editable = true;
              break;
            case "st2":
              component.width_editable = false;
              break;
          }
          break;
        case "1011": //flg1 + st1 + st2
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "st1":
              component.width_editable = true;
              break;
            case "st2":
              component.width_editable = false;
              break;
          }
          break;
        case "1101": //flg1 + flg2 + st2
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "flg2":
              component.width_editable = true;
              break;
            case "st2":
              component.width_editable = false;
              break;
          }
          break;
        case "1000": //flg1
          break;
        case "1001": //flg1 + st2
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "st2":
              component.width_editable = false;
              break;
          }
          break;
        case "1100": //flg1 + flg2
          switch (component.type) {
            case "flg1":
              component.width_editable = true;
              break;
            case "flg2":
              component.width_editable = false;
              break;
          }
          break;
      }
      return component;
    },

    calcInitComponentWidths() {
      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const flg2 = this.internalDoorType.components.find(
        component => component.type == "flg2"
      );
      const st1 = this.internalDoorType.components.find(
        component => component.type == "st1"
      );
      const st2 = this.internalDoorType.components.find(
        component => component.type == "st2"
      );
      const total_width =
        (flg1?.proof_width || 0) +
        (flg2?.proof_width || 0) +
        (st1?.proof_width || 0) +
        (st2?.proof_width || 0);

      const r_flg1 = flg1?.proof_width / total_width;
      const r_flg2 = flg2?.proof_width / total_width;
      const r_st1 = st1?.proof_width / total_width;
      const r_st2 = st2?.proof_width / total_width;

      let val = "1";

      flg2 ? (val += "1") : (val += "0");
      st1 ? (val += "1") : (val += "0");
      st2 ? (val += "1") : (val += "0");
      switch (val) {
        case "1010": //flg1 + st1
          this.formValues.components["flg1"].width = Math.round(
            this.clearWidth * r_flg1
          );
          this.formValues.components["st1"].width =
            this.clearWidth - this.formValues.components["flg1"].width;
          break;
        case "1110": //flg1 + flg2 + st1
          this.formValues.components["st1"].width = Math.round(
            this.clearWidth * r_st1
          );
          this.formValues.components["flg1"].width =
            (this.clearWidth - this.formValues.components["st1"].width) / 2;
          break;
        case "1111": //flg1 + flg2 + st1 + st2
          this.formValues.components["st1"].width = Math.round(
            this.clearWidth * r_st1
          );
          this.formValues.components["st2"].width = Math.round(
            this.clearWidth * r_st2
          );
          this.formValues.components["flg1"].width =
            (this.clearWidth -
              this.formValues.components["st1"].width -
              this.formValues.components["st2"].width) /
            2;
          this.formValues.components["flg2"].width =
            (this.clearWidth -
              this.formValues.components["st1"].width -
              this.formValues.components["st2"].width) /
            2;
          break;
        case "1011": //flg1 + st1 + st2
          this.formValues.components["st1"].width = Math.round(
            this.clearWidth * r_st1
          );
          this.formValues.components["st2"].width = Math.round(
            this.clearWidth * r_st2
          );
          this.formValues.components["flg1"].width =
            this.clearWidth -
            this.formValues.components["st1"].width -
            this.formValues.components["st2"].width;
          break;
        case "1101": //flg1 + flg2 + st2
          this.formValues.components["st2"].width = Math.round(
            this.clearWidth * r_st2
          );
          this.formValues.components["flg1"].width =
            (this.clearWidth - this.formValues.components["st2"].width) / 2;
          this.formValues.components["flg2"].width =
            (this.clearWidth - this.formValues.components["st2"].width) / 2;
          break;
        case "1000": //flg1
          this.formValues.components["flg1"].width = this.clearWidth;
          break;
        case "1001": //flg1 + st2
          this.formValues.components["st2"].width = Math.round(
            this.clearWidth * r_st2
          );
          this.formValues.components["flg1"].width =
            this.clearWidth - this.formValues.components["st2"].width;
          break;
        case "1100": //flg1 + flg2
          this.formValues.components["flg1"].width = Math.round(
            this.clearWidth * r_flg1
          );
          this.formValues.components["flg2"].width =
            this.clearWidth - this.formValues.components["flg1"].width;
          break;
        default:
          break;
      }
      this.glassPercentage();
    },
    calcInitComponentHeights() {
      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const ol = this.internalDoorType.components.find(
        component => component.type == "ol"
      );

      if (
        flg1 &&
        !this.formValues.components.flg1.height &&
        ol &&
        !this.formValues.components.ol.height
      ) {
        const total_height =
          (flg1?.proof_height || 0) + (ol?.proof_height || 0);

        const r_flg1 = flg1?.proof_height / total_height;

        this.$set(
          this.formValues.components.flg1,
          "height",
          Math.round(this.clearHeight * r_flg1)
        );
      }
      this.openHeight();
    },
    calcComponentWidths() {
      this.formValues = { ...this.formValues };

      const flg1 = this.internalDoorType.components.find(
        component => component.type == "flg1"
      );
      const flg2 = this.internalDoorType.components.find(
        component => component.type == "flg2"
      );
      const st1 = this.internalDoorType.components.find(
        component => component.type == "st1"
      );
      const st2 = this.internalDoorType.components.find(
        component => component.type == "st2"
      );

      let val = "1";

      flg2 ? (val += "1") : (val += "0");
      st1 ? (val += "1") : (val += "0");
      st2 ? (val += "1") : (val += "0");
      switch (val) {
        case "1010": //flg1 + st1
          this.$set(
            this.formValues.components.st1,
            "width",
            this.clearWidth - this.formValues.components["flg1"].width
          );

          break;
        case "1110": //flg1 + flg2 + st1
          this.$set(
            this.formValues.components.st1,
            "width",
            this.clearWidth -
              this.formValues.components["flg1"].width -
              this.formValues.components["flg2"].width
          );
          break;
        case "1111": //flg1 + flg2 + st1 + st2
          this.$set(
            this.formValues.components.st2,
            "width",
            this.clearWidth -
              this.formValues.components["st1"].width -
              this.formValues.components["flg1"].width -
              this.formValues.components["flg2"].width
          );
          break;
        case "1011": //flg1 + st1 + st2
          this.$set(
            this.formValues.components.st2,
            "width",
            this.clearWidth -
              this.formValues.components["st1"].width -
              this.formValues.components["flg1"].width
          );
          break;
        case "1101": //flg1 + flg2 + st2
          this.$set(
            this.formValues.components.st2,
            "width",
            this.clearWidth -
              this.formValues.components["flg1"].width -
              this.formValues.components["flg2"].width
          );
        case "1000": //flg1
          break;
        case "1001": //flg1 + st2
          this.$set(
            this.formValues.components.st2,
            "width",
            this.clearWidth - this.formValues.components["flg1"].width
          );
          break;
        case "1100": //flg1 + flg2
          this.$set(
            this.formValues.components.flg2,
            "width",
            this.clearWidth - this.formValues.components["flg1"].width
          );
          break;
      }
      this.calcOlWidth();
      this.openWidth();
      this.glassPercentage();
      this.prove();
    },

    selectFirstComponentFilling(data, component) {
      if (this.formValues.components[component.type].filling_id == null) {
        if (data.length) {
          this.formValues.components[component.type].filling_id = data[0].id;
        }
      }
    },

    openHeight() {
      if (this.formValues.components.flg1) {
        let ground_distance = 5;
        let tmp =
          this.formValues.components.flg1.height -
          this.doorType.overhang +
          ground_distance;
        if (Number.isNaN(tmp)) {
          this.formValues.open_height = null;
        }
        this.formValues.open_height = tmp;
      }
    },
    openWidth() {
      let flg1_width = 0;
      if (this.formValues.components.flg1) {
        flg1_width = parseInt(this.formValues.components.flg1.width);
      }
      let flg2_width = 0;
      if (this.formValues.components.flg2) {
        flg2_width = parseInt(this.formValues.components.flg2.width);
      }

      let tmp = flg1_width - 2 * this.doorType.overhang;
      if (this.formValues.components.flg1 && this.formValues.components.flg2) {
        tmp = flg1_width + flg2_width - 2 * this.doorType.overhang;
      }

      if (Number.isNaN(tmp)) {
        this.formValues.open_width = null;
      }
      this.formValues.open_width = tmp;
    }
  },
  watch: {
    doorType: function(val) {
      this.internalDoorType = val;
    }
  }
};
</script>

<style lang="scss" scoped></style>
